/**
 * ViewForwardedTickets - Proda-only view for forwarded tickets across all companies
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "@/ui/atoms";
import { cn } from "@/utils/utils";
import type { TApiSupportTicketResponse } from "@/apis/proda_finance/types";

const TYPE_COLORS: Record<string, string> = {
    bug: "bg-red-100 text-red-700",
    feature: "bg-purple-100 text-purple-700",
};

const PRIORITY_COLORS: Record<string, string> = {
    low: "bg-gray-100 text-gray-700",
    normal: "bg-blue-100 text-blue-700",
    high: "bg-red-100 text-red-700",
};

const PRIORITY_LABELS: Record<string, string> = {
    low: "Low",
    normal: "Normal",
    high: "High",
};

export type TViewForwardedTicketsParams = {
    useCases: () => {
        loadForwardedTickets: (params: {
            page: number;
            pageSize: number;
            companyId?: string;
        }) => Promise<{
            items: TApiSupportTicketResponse[];
            total: number;
        }>;
    };
};

export type TViewForwardedTicketsProps = Record<string, never>;

export const createViewForwardedTickets = ({ useCases }: TViewForwardedTicketsParams) => {
    return function ViewForwardedTickets() {
        const { loadForwardedTickets } = useCases();
        const navigate = useNavigate();
        const [searchParams, setSearchParams] = useSearchParams();

        const [tickets, setTickets] = useState<TApiSupportTicketResponse[]>([]);
        const [total, setTotal] = useState(0);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);

        const page = parseInt(searchParams.get("page") || "1", 10);
        const pageSize = 20;

        const fetchTickets = useCallback(async () => {
            setIsLoading(true);
            setError(null);
            try {
                const result = await loadForwardedTickets({ page, pageSize });
                setTickets(result.items);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load tickets");
            } finally {
                setIsLoading(false);
            }
        }, [loadForwardedTickets, page, pageSize]);

        useEffect(() => {
            fetchTickets();
        }, [fetchTickets]);

        return (
            <div className="space-y-6">
                <h1 className="text-2xl font-semibold">Forwarded Tickets</h1>
                <p className="text-sm text-muted-foreground">
                    Tickets forwarded to Proda from all companies.
                </p>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                        {error}
                    </div>
                )}

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        No forwarded tickets.
                    </div>
                ) : (
                    <>
                        <div className="rounded-md border">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="px-4 py-3 text-left font-medium">Title</th>
                                        <th className="px-4 py-3 text-left font-medium w-24">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-24">
                                            Priority
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-40">
                                            Company
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-40">
                                            Reporter
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-36">
                                            Forwarded At
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.map((ticket) => (
                                        <tr
                                            key={ticket.id}
                                            className="border-b cursor-pointer hover:bg-muted/30 transition-colors even:bg-muted/40"
                                            onClick={() => navigate(`/support/${ticket.id}`)}
                                        >
                                            <td className="px-4 py-3 font-medium">
                                                {ticket.title}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                                                        TYPE_COLORS[ticket.ticket_type] ||
                                                            "bg-gray-100 text-gray-700",
                                                    )}
                                                >
                                                    {ticket.ticket_type === "bug"
                                                        ? "Bug"
                                                        : "Feature"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                                                        PRIORITY_COLORS[ticket.priority] ||
                                                            "bg-gray-100 text-gray-700",
                                                    )}
                                                >
                                                    {PRIORITY_LABELS[ticket.priority] ||
                                                        ticket.priority}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {ticket.company_name || "Unknown"}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {ticket.created_by_display_name}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {ticket.forwarded_at
                                                    ? new Date(
                                                          ticket.forwarded_at,
                                                      ).toLocaleDateString()
                                                    : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            total={total}
                            onPageChange={(newPage) => {
                                const params = new URLSearchParams(searchParams);
                                params.set("page", String(newPage));
                                setSearchParams(params);
                            }}
                        />
                    </>
                )}
            </div>
        );
    };
};
